import Button from "components/Button/Button";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import Loader from "react-loader-spinner";
import GovernanceStore from "stores/GovernanceStore";
import { GovernanceFormValidations, TimeLock } from "types";
import getSubmitButton from "./getSubmitButton";
import { loaderColor } from "../../helpers/constants";
import "./GovernanceModal.scss";

const timelocks: TimeLock[] = [
  { lockTime: 2, multiplier: 1.5, level: 1, name: "Voter" },
  { lockTime: 4, multiplier: 3, level: 2, name: "Speaker" },
  { lockTime: 12, multiplier: 10, level: 3, name: "Council" },
  { lockTime: 24, multiplier: 25, level: 4, name: "Executive" },
];

function GovernanceModal({ userGovernanceData, YGYBalance }) {
  const [taxValue, setTaxValue] = useState(1);
  const [depositValue, setDepositValue] = useState<number | undefined>(undefined);
  const [depositAswell, setDepositAswell] = useState(true);
  const [timeLockChoice, setTimeLockChoice] = useState<TimeLock>(timelocks[0]);

  useEffect(() => {
    if (!depositAswell) {
      setDepositValue(0);
      setTimeLockChoice(timelocks[0]);
    }
  }, [depositAswell]);

  if (YGYBalance == null && !userGovernanceData) return <Loader type="Bars" color={loaderColor} />;
  const { number, timelockedYGY } = userGovernanceData;
  const { timeLocks } = GovernanceStore;

  const validations: GovernanceFormValidations = validateState(taxValue, number, timelockedYGY, YGYBalance);

  return (
    <div className="governance-modal">
      <div className="governance-modal-title">LP+ Treasury Funding</div>
      <div className="governance-modal-subtitle">
        Wallet: <span style={{ fontWeight: "bold", margin: "0px 4px" }}>{YGYBalance.toFixed(4)}</span>YGY
      </div>
      <div className="user-info-container">
        <div className="user-info">
          Treasury rate: <span className="amount"> {number}% tax </span>
        </div>
        <div className="user-info">
          Voting power: <span className="amount"> {timelockedYGY.toFixed(3)} </span> YGY
        </div>
      </div>
      <div className="timelock-container">
        <h3 style={{ marginBottom: "2px" }}>Timelock</h3>
        <span style={{ marginBottom: "7px", fontSize: "12px" }}>
          The minimum timelock period is 2 weeks
          <br />
        </span>
        <div className={`timelock-buttons flex-row ${!validations.canDeposit && "disabled"}`}>
          {timelocks.map((timelock, index) => (
            <Button
              key={`timelock-button-${timelock.name}`}
              type="normal"
              className={`timelock-button flex-row ${timeLockChoice === timelock && "active"} ${!depositAswell && "disabled"}`}
              onClick={() => {
                if (timeLockChoice !== timelock) {
                  setTimeLockChoice(timelock);
                } else {
                  setTimeLockChoice(timelocks[0]);
                }
              }}
            >
              {timelock.name}
            </Button>
          ))}
        </div>
        {timeLockChoice && timeLockChoice.lockTime > 0 && (
          <div className="timelock-info">
            {timeLockChoice.lockTime} weeks <br />
            {timeLockChoice.multiplier}x deposit weight
          </div>
        )}
      </div>
      <input
        className="section-interactions-input ygy-deposit-input"
        type="number"
        disabled={!depositAswell}
        value={depositValue}
        placeholder={!!timeLockChoice ? `Amount to deposit max: ${YGYBalance}` : "Set a withdraw/deposit amount"}
        onChange={(e) => {
          const amount = parseFloat(e.target.value);
          if (amount <= YGYBalance) {
            setDepositValue(amount);
          } else if (amount > YGYBalance) {
            setDepositValue(YGYBalance);
          } else {
            setDepositValue(undefined);
          }
        }}
      />
      <div className="slider-container">
        <div className="tax-rate-title">Adjust your proposed LP+ funding rate. ({taxValue}%)</div>
        <input
          name="tax-rate"
          type="range"
          min="1"
          max="8"
          value={taxValue}
          onChange={(e) => setTaxValue(parseFloat(e.target.value))}
          className="tax-slider"
        />
      </div>

      <Button
        type="neutral"
        className={`governance-button deposit-aswell ${!depositAswell && "active"}`}
        onClick={() => {
          setDepositAswell((val) => !val);
        }}
      >
        Only reweight your funding proposal{" "}
        {!depositAswell && (
          <span role="img" aria-label="checkmark">
            ✅
          </span>
        )}
      </Button>
      <div className="no-deposit-info">
        <i>
          <b>NOTE: </b>If you have no deposits - your vote will NOT have any weight
        </i>
      </div>
      <div className="governance-buttons buttons-unlock">
        {getSubmitButton(validations, taxValue, depositValue, depositAswell, timeLockChoice)}
      </div>
      <div className="buttons-unlock">
        {timelocks.map((item) => {
          const lockTime = timeLocks.get(item.level);
          const hasDeposit = lockTime && lockTime > 0;
          const canUnlock = hasDeposit && new Date(lockTime! * 1000).getTime() <= Date.now();
          return (
            <Button
              type="negative"
              className={`governance-button ${!canUnlock && "disabled"}`}
              onClick={() => GovernanceStore.unlockOldestYGY(item.level)}
            >
              Unlock {item.name} deposit
              <br />
              {lockTime && lockTime > 0 ? new Date(lockTime * 1000).toDateString() : null}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

const validateState = (taxValue: number, number: number, lockedYGY: number, YGYBalance: number): GovernanceFormValidations => {
  const canChangeTaxRate = taxValue !== number;
  const canDeposit = YGYBalance > 0;
  return {
    canChangeTaxRate,
    canDeposit,
    canWithdraw: canDeposit,
  };
};

export default observer(GovernanceModal);
